import React, { CSSProperties, FunctionComponent, useEffect, useState } from "react";
import { ListItemIcon, Menu, MenuItem, useTheme } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import { IAlbum, SellStatus } from "../../../hooks/albums";
import { useContextMenu } from "../../../hooks/contextMenu";
import { AlbumUtilities } from "../../../models/AlbumUtilities";
import { toast } from "react-toastify";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { useCollectionManager } from "../../../hooks/collections/useCollectionManager";
import { useGetVideoIds } from "../../../hooks/youTube/useGetVideoIds";
import { useGetPrivateLink } from "../../../hooks/share/useGetPrivateLink";
import { useUser } from "../../../hooks/session";
import { useShareLink } from "../../../hooks/share/useShareLink";
import { StringFormat } from "../../../models/StringFormat";
import { AlertDialog } from "../../AlertDialog";
import { useDeleteAlbum } from "../../../hooks/albums/useDeleteAlbum";
import { ToolbarRawButton } from "../../toolbar/ToolbarRawButton";
import { useRemoveAlbumFromBoxSet } from "../../../hooks/boxSets";
import { useOpenWindow } from "../../../hooks/openWindow";
import { useIsMobile, useIsPiperiOSApp } from "../../../hooks/isMobile";
import { Icon, IconType } from "../../../icons";
import { useAlbumFilterManager } from "../../../hooks/albums/useAlbumFilterManager";

export const AlbumOptionsButton: FunctionComponent<IAlbumOptionsButtonProps> = ({
  isPublic,
  className,
  buttonClassName,
  style,
  album,
  userEvent,
  onCopy,
  onPlayFromYouTube,
  onMarkAs,
  onOpenInLayout,
  onDelete,
  onDeleteFromBoxSet,
  isForLayout,
  isBoxSetAlbum,
  boxSetAlbumId,
  setExternalLoading
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isPiperiOSApp = useIsPiperiOSApp();
  const [currentUser] = useUser.useState();
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getCurrentCollection, isCurrentCollectionShareable } = useCollectionManager();
  const { getCurrentIsNegated } = useAlbumFilterManager(isForLayout);
  const [qrFilename, setQRFilename] = useState("");
  const { deleteAlbum } = useDeleteAlbum();
  const { removeAlbumFromBoxSet } = useRemoveAlbumFromBoxSet();
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false);
  const [isOpenDeleteFromBoxSetConfirmation, setIsOpenDeleteFromBoxSetConfirmation] =
    useState(false);
  const [shareLinkComponents, fetchShareLink, fetchPrivateLink, isShareLoading] =
    useShareLink(qrFilename);
  const getVideoIds = useGetVideoIds();
  const { getPrivateLink } = useGetPrivateLink();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  useEffect(() => {
    if (!userEvent || isShareLoading) return;
    handleContextMenu(userEvent);
  }, [userEvent]);
  useEffect(() => setLoading(isShareLoading), [isShareLoading]);
  useEffect(() => {
    if (setExternalLoading) {
      setExternalLoading(loading);
    }
  }, [loading]);
  const {
    menuProps: reservedMenuProps,
    handleContextMenu: reservedHandleContextMenu,
    handleMenuClose: reservedHandleMenuClose
  } = useContextMenu();
  const shareAlbum = async () => {
    const collectionId = getCurrentCollection(isPublic)?.uuid;
    if (!collectionId) return;
    setQRFilename(album.name);
    await fetchShareLink(collectionId, album.uuid);
  };
  const playFromYouTube = async () => {
    if (!album.trackList) return;
    setLoading(true);
    const videoIds = await getVideoIds(album.trackList, album.name, album.artist?.name);
    setLoading(false);
    if (videoIds.length > 0) {
      onPlayFromYouTube?.(videoIds);
    } else {
      toast.error(strings.player_unknown_error);
    }
  };
  const openOnDiscogs = () => {
    if (!album.discogsReleaseId) {
      return;
    }
    openInNewTab(`https://www.discogs.com/release/${album.discogsReleaseId}`);
  };
  const openOnPrivateCollection = async () => {
    setLoading(true);
    const { status, body } = await getPrivateLink({ albumId: album.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      openInNewTab(body.shareLink);
    } else {
      toast.error(body.message);
    }
    setLoading(false);
  };
  const { openInNewTab } = useOpenWindow();
  return (
    <div className={className} style={style}>
      <ToolbarRawButton
        tooltip={strings.options}
        iconType={IconType.MoreVertical}
        onClick={handleContextMenu}
        loading={loading || isShareLoading}
        buttonClassName={buttonClassName}
      />
      <Menu {...menuProps}>
        {isForLayout && (
          <MenuItem
            key={"OpenInLayout"}
            onClick={() => {
              handleMenuClose();
              onOpenInLayout();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.Shelves} />
            </ListItemIcon>
            {strings.open_on_layout}
          </MenuItem>
        )}
        {!isPublic && (
          <MenuItem
            key={"PrivateLink"}
            onClick={() => {
              handleMenuClose();
              setQRFilename(album.name);
              fetchPrivateLink(album.uuid).then();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.QRCode2} />
            </ListItemIcon>
            {strings.get_private_link}
          </MenuItem>
        )}
        {isCurrentCollectionShareable(isPublic) &&
          !isBoxSetAlbum &&
          (isPublic || !getCurrentIsNegated().collectionId) && (
            <MenuItem
              key={"Share"}
              onClick={() => {
                handleMenuClose();
                shareAlbum().then();
              }}
              dense
            >
              <ListItemIcon>
                <Icon type={IconType.Share} />
              </ListItemIcon>
              {strings.share_album}
            </MenuItem>
          )}
        {!isForLayout && !isBoxSetAlbum && onCopy && (
          <MenuItem
            key={"Copy"}
            onClick={async () => {
              handleMenuClose();
              onCopy?.();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.TabInactive} />
            </ListItemIcon>
            {album.isBoxSet ? strings.copy_box_set : strings.copy_album}
          </MenuItem>
        )}
        {onMarkAs &&
          !isPublic &&
          !isForLayout &&
          (album.sellStatus ? (
            <MenuItem
              key={"RemoveMark"}
              onClick={async () => {
                handleMenuClose();
                onMarkAs(null);
              }}
              dense
            >
              <ListItemIcon>
                <Icon type={IconType.KeepOff} />
              </ListItemIcon>
              {strings.remove_mark_title}
            </MenuItem>
          ) : (
            <MenuItem key={"AddMark"} onClick={reservedHandleContextMenu} dense>
              <ListItemIcon>
                <Icon type={IconType.Keep} />
              </ListItemIcon>
              {strings.mark_as_title}
            </MenuItem>
          ))}
        {!isMobile && album.trackList && album.trackList.length > 0 && onPlayFromYouTube && (
          <MenuItem
            key={"Play"}
            onClick={async () => {
              handleMenuClose();
              await playFromYouTube();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.PlayCircle} />
            </ListItemIcon>
            {strings.play_from_youtube}
          </MenuItem>
        )}
        {album.discogsReleaseId && (
          <MenuItem
            key={"Discogs"}
            onClick={() => {
              handleMenuClose();
              openOnDiscogs();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.OpenInNew} />
            </ListItemIcon>
            {strings.open_on_discogs}
          </MenuItem>
        )}
        {!isPiperiOSApp &&
          (isPublic || isForLayout) &&
          currentUser &&
          (AlbumUtilities.isOwner(album, currentUser) || isForLayout) && (
            <MenuItem
              key={"OpenOnPrivateCollection"}
              onClick={async () => {
                handleMenuClose();
                await openOnPrivateCollection();
              }}
              dense
            >
              <ListItemIcon>
                <Icon type={IconType.FrameInspect} />
              </ListItemIcon>
              {isForLayout ? strings.option_view_detail : strings.open_on_private_collection}
            </MenuItem>
          )}
        {!isPublic && !isForLayout && !isBoxSetAlbum && onDelete && (
          <MenuItem
            key={"Remove"}
            onClick={() => {
              handleMenuClose();
              setIsOpenDeleteConfirmation(true);
            }}
            sx={{ color: theme.palette.colors?.remove }}
            dense
          >
            <ListItemIcon sx={{ color: theme.palette.colors?.remove }}>
              <Icon type={IconType.DeleteForever} />
            </ListItemIcon>
            {strings.alert_delete}
          </MenuItem>
        )}
        {!isPublic && !isForLayout && isBoxSetAlbum && (
          <MenuItem
            key={"RemoveFromBoxSet"}
            onClick={() => {
              handleMenuClose();
              setIsOpenDeleteFromBoxSetConfirmation(true);
            }}
            sx={{ color: theme.palette.colors?.remove }}
            dense
          >
            <ListItemIcon sx={{ color: theme.palette.colors?.remove }}>
              <Icon type={IconType.DeleteForever} />
            </ListItemIcon>
            {strings.album_remove_from_box_set}
          </MenuItem>
        )}
      </Menu>
      <Menu {...reservedMenuProps}>
        {AlbumUtilities.getAllSellStatuses().map(sellStatus => (
          <MenuItem
            key={`${sellStatus}`}
            onClick={() => {
              handleMenuClose();
              reservedHandleMenuClose();
              onMarkAs(sellStatus);
            }}
            dense
          >
            {AlbumUtilities.getSellStatusDescription(sellStatus)}
          </MenuItem>
        ))}
      </Menu>
      <AlertDialog
        message={
          album.isBoxSet
            ? StringFormat(strings.box_set_delete_confirmation, album.name)
            : StringFormat(strings.album_delete_confirmation, album.name)
        }
        open={isOpenDeleteConfirmation}
        setOpen={setIsOpenDeleteConfirmation}
        isConfirm
        onConfirm={async () => {
          setLoading(true);
          const { status, body } = await deleteAlbum({ uuid: album.uuid });
          if (!StatusCodesHelper.isSuccessful(status)) {
            setAlertMessage(body.message);
            setAlertIsOpen(true);
          } else {
            onDelete?.();
          }
          setLoading(false);
        }}
      />
      <AlertDialog
        message={StringFormat(strings.album_remove_from_box_set_confirmation, album.name)}
        open={isOpenDeleteFromBoxSetConfirmation}
        setOpen={setIsOpenDeleteFromBoxSetConfirmation}
        isConfirm
        onConfirm={async () => {
          if (!boxSetAlbumId) return;
          setLoading(true);
          const { status, body } = await removeAlbumFromBoxSet({
            boxSetAlbumId: boxSetAlbumId,
            albumId: album.uuid
          });
          if (!StatusCodesHelper.isSuccessful(status)) {
            setAlertMessage(body.message);
            setAlertIsOpen(true);
          } else {
            onDeleteFromBoxSet?.(body);
          }
          setLoading(false);
        }}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      {shareLinkComponents()}
    </div>
  );
};

export interface IAlbumOptionsButtonProps {
  isPublic: boolean;
  className?: string;
  buttonClassName?: string;
  style?: CSSProperties;
  album: IAlbum;
  userEvent: React.MouseEvent | React.TouchEvent | undefined;
  onCopy: () => void;
  onPlayFromYouTube?: (videoIds: string[]) => void;
  onMarkAs?: (sellStatus: SellStatus | null) => void;
  onOpenInLayout?: () => void;
  onDelete?: () => void;
  onDeleteFromBoxSet?: (newBoxSet: IAlbum) => void;
  isForLayout?: boolean;
  isBoxSetAlbum?: boolean;
  boxSetAlbumId?: string;
  setExternalLoading?: (isLoading: boolean) => void;
}
